import { MANAGED_LEAD_PRODUCTS } from '../../config/managedLeadNavigation';
import {
  ManagedLeadNote,
  ManagedLeadOwner,
  ManagedLeadProductPath,
} from '../../services/api/managed-leads/managedLeads';
import { getManagedLeadFieldValue } from './managedLeadFormatting';

const SAFE_NOTE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const SAFE_NOTE_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
]);
const BLOCKED_NOTE_TAGS = new Set([
  'script',
  'style',
  'template',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'img',
]);
/**
 * The allowlist is derived from the routes the product actually registers, so
 * renaming a list route cannot leave a stale destination behind.
 */
const listRoutesOf = (product: ManagedLeadProductPath) => {
  const categories =
    MANAGED_LEAD_PRODUCTS.find((entry) =>
      entry.activePaths.includes(`/${product}`)
    )?.categories ?? [];

  return {
    safePaths: new Set(categories.map((category) => category.path)),
    fallback:
      categories.find((category) => category.id === 'in-progress')?.path ??
      `/${product}/in-progress`,
  };
};

const LIST_ROUTES: Record<
  ManagedLeadProductPath,
  ReturnType<typeof listRoutesOf>
> = {
  prestiti: listRoutesOf('prestiti'),
  'cessioni-del-quinto': listRoutesOf('cessioni-del-quinto'),
};

/**
 * Resolves the detail "back to the list" destination: only an internal list
 * route of the same product survives, so a crafted `returnTo` can neither leave
 * the backoffice nor cross into another product's list.
 */
export const safeManagedLeadReturnTo = (
  product: ManagedLeadProductPath,
  candidate: string | null,
  dealId: string
) => {
  const { safePaths, fallback } = LIST_ROUTES[product];
  let destination = fallback;

  if (candidate && candidate.startsWith('/') && !candidate.startsWith('//')) {
    try {
      const url = new URL(candidate, window.location.origin);
      if (
        url.origin === window.location.origin &&
        safePaths.has(url.pathname)
      ) {
        destination = `${url.pathname}${url.search}`;
      }
    } catch {
      destination = fallback;
    }
  }

  return `${destination}#managed-lead-${encodeURIComponent(dealId)}`;
};

export const sanitizeManagedLeadNote = (html: string) => {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const sanitized = document.createElement('div');

  const appendSanitizedChildren = (source: Node, target: Node) => {
    source.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        target.appendChild(document.createTextNode(child.textContent ?? ''));
        return;
      }
      if (!(child instanceof HTMLElement)) return;

      const tagName = child.tagName.toLocaleLowerCase('en-US');
      if (BLOCKED_NOTE_TAGS.has(tagName)) return;
      if (!SAFE_NOTE_TAGS.has(tagName)) {
        appendSanitizedChildren(child, target);
        return;
      }

      const safeElement = document.createElement(tagName);
      if (tagName === 'a') {
        const href = child.getAttribute('href')?.trim();
        if (href) {
          try {
            const url = new URL(href, window.location.origin);
            const isInternalRelativeLink =
              href.startsWith('/') && !href.startsWith('//');
            if (
              isInternalRelativeLink ||
              SAFE_NOTE_PROTOCOLS.has(url.protocol)
            ) {
              safeElement.setAttribute('href', href);
            }
          } catch {
            // An invalid href is intentionally rendered as plain text.
          }
        }
      }

      appendSanitizedChildren(child, safeElement);
      if (tagName === 'a' && !safeElement.hasAttribute('href')) {
        while (safeElement.firstChild) {
          target.appendChild(safeElement.firstChild);
        }
      } else {
        target.appendChild(safeElement);
      }
    });
  };

  appendSanitizedChildren(parsed.body, sanitized);
  return sanitized.innerHTML;
};

const managedLeadNoteAuthor = (note: ManagedLeadNote) => {
  const author = getManagedLeadFieldValue(note.author);
  if (author === null || typeof author !== 'object') return null;

  return author as ManagedLeadOwner;
};

/**
 * Who may read a Nota, the same rule as the Mutui notes: an admin reads every
 * Nota of the lead, anybody else only the ones they wrote or that the
 * consultant they are impersonating wrote. A Nota nobody signs is shown to
 * admins only.
 */
export const visibleManagedLeadNotes = (
  notes: ManagedLeadNote[],
  { isAdmin, viewerEmails }: { isAdmin: boolean; viewerEmails: string[] }
) => {
  if (isAdmin) return notes;

  const allowedEmails = new Set(
    viewerEmails.map((email) => email.trim().toLowerCase()).filter(Boolean)
  );

  return notes.filter((note) => {
    const authorEmail = managedLeadNoteAuthor(note)?.email?.trim().toLowerCase();
    return !!authorEmail && allowedEmails.has(authorEmail);
  });
};
